import { decodeErrorResult, type Abi, type Address } from "viem";
import { ERROR_SELECTOR_TO_NAME, withCommonErrors } from "./commonErrors";

export type DecodedContractError = {
  errorName?: string;
  reason?: string;
  selector?: string;
  functionName?: string;
  originalName?: string;
  message: string;
};

type WriteSimulateArgs = {
  address: Address;
  abi: Abi;
  functionName: string;
  args?: readonly unknown[];
  account?: unknown;
  value?: bigint;
};

const USER_REJECTED_PATTERN =
  /user rejected|user denied|rejected the request/i;

const HEX_DATA_PATTERN = /^0x[0-9a-f]*$/i;
const ERROR_STRING_SELECTOR = "0x08c379a0";
const USELESS_REASON_PATTERN =
  /^(error|execution reverted|unknown reason|missing revert data|reverted)$/i;

const isBuiltinErrorName = (name?: string) =>
  name === "Error" || name === "Panic";

const isUsefulReason = (reason?: string) =>
  !!reason && !USELESS_REASON_PATTERN.test(reason.trim());

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === "object" ? (value as Record<string, unknown>) : null;

const isHexData = (value: unknown): value is string =>
  typeof value === "string" && HEX_DATA_PATTERN.test(value) && value.length >= 10;

const collectRevertHex = (error: unknown): string[] => {
  const results: string[] = [];
  const seen = new Set<unknown>();

  const pushHex = (value: unknown) => {
    if (!isHexData(value)) return;
    const normalized = value.toLowerCase();
    if (!results.includes(normalized)) results.push(normalized);
  };

  const visit = (value: unknown, depth: number) => {
    if (value == null || depth > 6 || seen.has(value)) return;
    if (typeof value === "string") {
      pushHex(value);
      return;
    }
    if (typeof value !== "object") return;
    seen.add(value);

    const obj = asRecord(value);
    if (!obj) return;

    pushHex(obj.data);
    pushHex(obj.raw);
    if (asRecord(obj.data)) {
      pushHex(asRecord(obj.data)?.data);
    }

    visit(obj.cause, depth + 1);
    visit(obj.error, depth + 1);
    visit(obj.data, depth + 1);

    if (typeof obj.body === "string") {
      try {
        visit(JSON.parse(obj.body), depth + 1);
      } catch {
        // body is not JSON
      }
    }

    if (typeof obj.walk === "function") {
      try {
        (obj.walk as (fn: (err: unknown) => unknown) => unknown)((nested) => {
          visit(nested, depth + 1);
          return nested;
        });
      } catch {
        // ignore walk failures on non-viem errors
      }
    }
  };

  visit(error, 0);

  const rankHex = (hex: string) => {
    const selector = hex.slice(0, 10);
    if (selector === ERROR_STRING_SELECTOR && hex.length > 10) return 0;
    if (ERROR_SELECTOR_TO_NAME[selector]) return 1;
    if (selector === ERROR_STRING_SELECTOR) return 2;
    return 3;
  };

  return results.sort((a, b) => rankHex(a) - rankHex(b) || a.length - b.length);
};

const readString = (error: unknown, key: string): string | undefined => {
  const obj = asRecord(error);
  const value = obj?.[key];
  return typeof value === "string" && value ? value : undefined;
};

const findNestedString = (error: unknown, key: string): string | undefined => {
  const seen = new Set<unknown>();

  const visit = (value: unknown, depth: number): string | undefined => {
    if (value == null || depth > 5 || seen.has(value) || typeof value !== "object") {
      return undefined;
    }
    seen.add(value);
    const obj = value as Record<string, unknown>;
    if (typeof obj[key] === "string" && obj[key]) return obj[key] as string;
    return (
      visit(obj.cause, depth + 1) ||
      visit(obj.error, depth + 1) ||
      visit(obj.data, depth + 1)
    );
  };

  return visit(error, 0);
};

const findFirstStringArg = (error: unknown): string | undefined => {
  const seen = new Set<unknown>();

  const visit = (value: unknown, depth: number): string | undefined => {
    if (value == null || depth > 6 || seen.has(value) || typeof value !== "object") {
      return undefined;
    }
    seen.add(value);
    const obj = value as Record<string, unknown>;
    if (Array.isArray(obj.args) && typeof obj.args[0] === "string" && isUsefulReason(obj.args[0])) {
      return obj.args[0];
    }
    return (
      visit(obj.cause, depth + 1) ||
      visit(obj.error, depth + 1) ||
      visit(obj.data, depth + 1)
    );
  };

  return visit(error, 0);
};

const collectText = (error: unknown): string => {
  const parts: string[] = [];
  const seen = new Set<unknown>();

  const visit = (value: unknown, depth: number) => {
    if (value == null || depth > 5 || seen.has(value)) return;
    if (typeof value === "string" || typeof value === "number") {
      parts.push(String(value));
      return;
    }
    if (typeof value !== "object") return;
    seen.add(value);
    const obj = value as Record<string, unknown>;
    for (const key of [
      "name",
      "shortMessage",
      "message",
      "details",
      "reason",
      "errorName",
      "code",
    ]) {
      if (typeof obj[key] === "string" && obj[key]) {
        parts.push(obj[key] as string);
      }
    }
    visit(obj.cause, depth + 1);
    visit(obj.error, depth + 1);
  };

  visit(error, 0);
  return parts.join("\n");
};

export const looksLikeUserRejected = (error: unknown) => {
  const name = readString(error, "name");
  if (name === "UserRejectedRequestError") return true;
  return USER_REJECTED_PATTERN.test(collectText(error));
};

const decodeHex = (data: string, abi: Abi): { errorName?: string; reason?: string } => {
  try {
    const decoded = decodeErrorResult({ abi, data: data as `0x${string}` });
    if (decoded.errorName === "Error") {
      const reason = decoded.args?.[0];
      return {
        errorName: undefined,
        reason: typeof reason === "string" ? reason : undefined,
      };
    }
    if (decoded.errorName === "Panic") {
      return { errorName: "Panic", reason: "The contract panicked during this call." };
    }
    return { errorName: decoded.errorName };
  } catch {
    const selector = data.slice(0, 10).toLowerCase();
    const errorName = ERROR_SELECTOR_TO_NAME[selector];
    return errorName ? { errorName } : {};
  }
};

const extractSelectorFromText = (text: string): string | undefined => {
  const patterns = [
    /unable to decode signature ['"]?(0x[0-9a-f]{8})['"]?/i,
    /reverted with the following signature:\s*(0x[0-9a-f]{8})\b/i,
    /custom error (0x[0-9a-f]{8})\b/i,
  ];
  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (match?.[1]) return match[1].toLowerCase();
  }

  const tokens = text.match(/0x[0-9a-f]{8}(?![0-9a-f])/gi) ?? [];
  for (const token of tokens) {
    const selector = token.toLowerCase();
    if (ERROR_SELECTOR_TO_NAME[selector]) return selector;
  }
  return undefined;
};

export const decodeContractError = (
  error: unknown,
  abi?: Abi | readonly unknown[]
): DecodedContractError => {
  const mergedAbi = withCommonErrors(abi);
  const text = collectText(error);
  const originalName = findNestedString(error, "name");
  const functionName =
    findNestedString(error, "functionName") ||
    text.match(/contract function ["']([^"']+)["']/)?.[1];

  const fromFields = {
    errorName: findNestedString(error, "errorName"),
    reason: findNestedString(error, "reason"),
  };

  let errorName = isBuiltinErrorName(fromFields.errorName)
    ? undefined
    : fromFields.errorName;
  let reason = isUsefulReason(fromFields.reason) ? fromFields.reason : undefined;
  let selector: string | undefined;

  if (!reason) {
    reason = findFirstStringArg(error);
  }

  for (const hex of collectRevertHex(error)) {
    selector = selector ?? hex.slice(0, 10);
    const decoded = decodeHex(hex, mergedAbi);
    if (decoded.errorName && !isBuiltinErrorName(decoded.errorName) && !errorName) {
      errorName = decoded.errorName;
    }
    if (decoded.reason && isUsefulReason(decoded.reason) && !reason) {
      reason = decoded.reason;
    }
    if (errorName || reason) break;
  }

  if (!errorName) {
    selector = selector ?? extractSelectorFromText(text);
    if (selector && ERROR_SELECTOR_TO_NAME[selector]) {
      errorName = ERROR_SELECTOR_TO_NAME[selector];
    }
  }

  const message = readString(error, "shortMessage") || readString(error, "message") || text;

  return {
    errorName,
    reason,
    selector,
    functionName,
    originalName,
    message: message || "The contract rejected this action.",
  };
};

export const toSerializableContractError = (
  decoded: DecodedContractError,
  original?: unknown
): Error => {
  if (looksLikeUserRejected(original) || looksLikeUserRejected(decoded.message)) {
    const rejected = new Error(decoded.message || "User rejected the request.");
    rejected.name = "UserRejectedRequestError";
    return rejected;
  }

  const functionLabel = decoded.functionName
    ? `The contract function "${decoded.functionName}" reverted`
    : "The contract function reverted";

  let message: string;
  let code: string | undefined;

  if (decoded.errorName && !isBuiltinErrorName(decoded.errorName)) {
    message = `${functionLabel}.\n\nError: ${decoded.errorName}()`;
    code = decoded.errorName;
  } else if (decoded.reason) {
    message = `${functionLabel} with the following reason:\n${decoded.reason}`;
  } else {
    message = decoded.message;
  }

  const error = new Error(message);
  error.name =
    (decoded.errorName && !isBuiltinErrorName(decoded.errorName)) || decoded.reason
      ? "ContractFunctionRevertedError"
      : decoded.originalName || "Error";
  if (code) {
    (error as Error & { code?: string }).code = code;
  }
  return error;
};

export const resolveContractWriteError = async ({
  error,
  clients,
  writeArgs,
}: {
  error: unknown;
  clients: Array<{ simulateContract: (args: any) => Promise<unknown> }>;
  writeArgs: WriteSimulateArgs;
}): Promise<never> => {
  if (looksLikeUserRejected(error)) {
    throw toSerializableContractError(decodeContractError(error, writeArgs.abi), error);
  }

  const decoded = decodeContractError(error, writeArgs.abi);
  if (decoded.errorName || decoded.reason) {
    throw toSerializableContractError(
      { ...decoded, functionName: decoded.functionName || writeArgs.functionName },
      error
    );
  }

  for (const client of clients) {
    try {
      await client.simulateContract({
        address: writeArgs.address,
        abi: writeArgs.abi,
        functionName: writeArgs.functionName,
        args: writeArgs.args,
        account: writeArgs.account as Address | undefined,
        value: writeArgs.value,
      });
    } catch (simError) {
      const simulated = decodeContractError(simError, writeArgs.abi);
      if (simulated.errorName || simulated.reason) {
        throw toSerializableContractError(
          {
            ...simulated,
            functionName: simulated.functionName || writeArgs.functionName,
          },
          simError
        );
      }
    }
  }

  throw toSerializableContractError(
    { ...decoded, functionName: decoded.functionName || writeArgs.functionName },
    error
  );
};
